// components/checklist/UserCreatedChecklistsInProject.tsx
import "ag-grid-enterprise";
import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import moment from "moment";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import { Alert, Button, Progress } from "reactstrap";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridOptions } from "ag-grid-community";

import { IChecklist, IChecklistProgress } from "@mctypes/checklist.types";
import { useAppSelector } from "../../../../hooks";
import { DisplayDueDate, FailedAlert, MCLoader, McInput, HSpace } from "../../../../components";
import { AG_GRID_LOCALE_NOR } from "../../../../agGridLocale";
import { STANDARD_COLORS } from "../../../globalConstants";
import { StatusBadgeRenderer } from "../../checklist/ChecklistColums";
import { useGetActiveProjectChecklistsCreatedByLoggedInUserQuery } from "../../checklist/checklistRTK";
import { CHECKLIST_STATES_OPTIONS, CHECKLIST_STATES_OPTIONS_COLOR } from "../../checklist/constants";

// ----- typer -----
type Row = IChecklist;

// ----- kolumner -----
const createColumns = (goToChecklist: (row: Row) => void): ColDef<Row>[] => [
  {
    field: "status",
    headerName: "Status",
    width: 160,
    editable: false,
    filter: true,
    valueGetter: (p) => {
      const base = CHECKLIST_STATES_OPTIONS.find((opt) => opt.value === p.data.status);
      const colored = CHECKLIST_STATES_OPTIONS_COLOR.find((opt) => opt.value === base?.displayValue);
      return colored ?? base ?? p.data.status;
    },
    cellRenderer: StatusBadgeRenderer,
  },
  {
    field: "name",
    headerName: "Sjekklister",
    minWidth: 220,
    flex: 2,
    editable: false,
    filter: "agTextColumnFilter",
    cellRenderer: (p) => (
      <div className="d-flex align-items-center">
        <Button className="p-0 m-0" onClick={() => goToChecklist(p.data)} color="link">
          {p.value}
        </Button>
        <HSpace />
      </div>
    ),
  },
  {
    field: "progress",
    headerName: "Fremskritt",
    minWidth: 180,
    editable: false,
    sortable: false,
    filter: false,
    cellRenderer: (p) => {
      const progress = p.value as IChecklistProgress | undefined;
      if (!progress) return "-";
      return (
        <div className="w-100">
          <Progress multi className="thin-progress">
            <Progress bar style={{ backgroundColor: STANDARD_COLORS.green }} value={progress.accepted} />
            <Progress bar style={{ backgroundColor: STANDARD_COLORS.red }} value={progress.rejected} />
          </Progress>
        </div>
      );
    },
  },
  {
    field: "dueDate",
    headerName: "Frist",
    width: 160,
    editable: false,
    filter: "agDateColumnFilter",
    cellRenderer: (p) => {
      const v = p.value as string | undefined;
      if (!v || !moment(v).isValid()) return "-";
      return <DisplayDueDate dueDate={v} isComplete={p.data.status === "DELIVERED"} />;
    },
  },
];

const UserCreatedChecklistsInProject: React.FC = () => {
  const projectId = useAppSelector((s) => s.globalReducer.currentProjectId); // kan være undefined
  const userId = useAppSelector((s) => s.globalReducer.user!._id);

  const history = useHistory();
  const gridRef = useRef<AgGridReact<Row>>(null);
  const [search, setSearch] = useState("");

  const {
    data = [],
    isLoading,
    isFetching,
    error,
  } = useGetActiveProjectChecklistsCreatedByLoggedInUserQuery({
    projectId: projectId ?? undefined,
    userId
  });

  const rows: Row[] = data ?? [];

  const goToChecklist = useCallback(
    (checklist: Row) => {
      if (checklist) {
        history.push(`/project/${checklist.project}/checklist/instance/${checklist._id}`);
      }
    },
    [history],
  );

  const columns = useMemo(() => createColumns(goToChecklist), [goToChecklist]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      minWidth: 140,
      suppressHeaderMenuButton: true,
      autoHeight: true,
      cellStyle: { whiteSpace: "normal", lineHeight: "1.5", wordBreak: "break-word" },
    }),
    [],
  );

  const getRowId = useCallback((p: { data: Row }) => p.data._id, []);

  const gridOptions = useMemo<GridOptions<Row>>(
    () => ({
      maintainColumnOrder: true,
      suppressColumnMoveAnimation: true,
    }),
    [],
  );

  // Vis gridens overlay mens data refetches (behold radene synlige)
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    if (isFetching) api.showLoadingOverlay();
    else api.hideOverlay();
  }, [isFetching]);

  return (
    <>
      {/* Søk */}
      <div className="pt-2 pb-1">
        <McInput
          placeholder="Søk…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 280 }}
        />
      </div>

      {isLoading && <MCLoader loading />}
      {!!error && <FailedAlert errorMessage="Kunne ikke laste inn dine sjekklister" />}

      {!error && !isLoading && rows.length === 0 && (
        <div className="flex-fill overflow-auto px-1">
          <Alert color="success" className="mt-5">
            <i className="fa fa-check fa-fw" />
            Du har ingen sjekklister{projectId ? " i dette prosjektet" : ""}.{" "}
            {projectId ? (
              <Link to={`/project/${projectId}/checklist/instance`}>Gå til sjekklisteappen</Link>
            ) : (
              <Link to={`/checklist/instance`}>Gå til sjekklisteappen</Link>
            )}{" "}
            for å se alle sjekklister.
          </Alert>
        </div>
      )}

      {!error && (
        <div className="ag-theme-quartz h-100 w-100">
          <AgGridReact<Row>
            ref={gridRef}
            rowData={rows}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            gridOptions={gridOptions}
            getRowId={getRowId}
            suppressHeaderFocus
            cellSelection
            quickFilterText={search}
            localeText={AG_GRID_LOCALE_NOR}
          />
        </div>
      )}
    </>
  );
};

export default UserCreatedChecklistsInProject;
